/**
 * Single Row Template Generate Functions
 *
 * Generates building and unit configurations from parameters
 */

import type { GeneratedBuilding, GeneratedUnit, DoorConfig, DoorSpec, DoorWall } from '../types'
import type { SingleRowParams } from './params'
import { getUnitDoors } from './params'
import {
  calculateUnitWidthsWithOverrides,
  calculateUnitOffsets,
  createBoxUnitEdges,
} from '../base'

/**
 * Generate building configuration from single-row parameters
 */
export function generateBuilding(params: SingleRowParams): GeneratedBuilding {
  // Convert bearing from degrees to radians
  const rotationRadians = (params.bearing_deg * Math.PI) / 180

  // Calculate unit widths for door configuration
  const { widths } = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.unit_count,
    params.wall_thickness_ft,
    params.unit_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Build door configs for all units (all walls)
  const doorConfigs: DoorConfig[] = []
  const offsets = calculateUnitOffsets(widths, params.wall_thickness_ft)

  for (let i = 0; i < params.unit_count; i++) {
    const unitDoors = getUnitDoors(params, i, widths[i], params.unit_count)

    // Helper to add doors from a wall
    const addDoorsFromWall = (wall: DoorWall, doors: DoorSpec[] | undefined) => {
      if (!doors) return
      for (const door of doors) {
        // Calculate absolute offset based on wall
        let absoluteOffset: number
        if (wall === 'front') {
          // Front wall: offset from unit start along building length
          absoluteOffset = offsets[i] + door.offset_ft
        } else if (wall === 'left') {
          // Left wall: offset from building front along building depth
          absoluteOffset = door.offset_ft
        } else if (wall === 'right') {
          // Right wall: offset from building front along building depth
          absoluteOffset = door.offset_ft
        } else {
          // Back wall: offset from unit start along building length
          absoluteOffset = offsets[i] + door.offset_ft
        }

        doorConfigs.push({
          wall,
          width_ft: door.width_ft,
          height_ft: door.height_ft,
          offset_ft: absoluteOffset,
          type: door.type,
        })
      }
    }

    // Add doors from all walls
    addDoorsFromWall('front', unitDoors.front)
    addDoorsFromWall('back', unitDoors.back)
    addDoorsFromWall('left', unitDoors.left)
    addDoorsFromWall('right', unitDoors.right)
  }

  return {
    name: params.name,
    code: params.code,
    type: 'box_hangar_single_row',
    exterior_width: params.width_ft,
    exterior_depth: params.depth_ft,
    exterior_height: params.height_ft,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: params.default_door_width_ft, // Primary door width
    door_height: params.default_door_height_ft,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: doorConfigs,
    },
    type_config: {
      template: 'single_row',
      unit_count: params.unit_count,
      wall_thickness_ft: params.wall_thickness_ft,
      calculated_unit_widths: widths,
      default_door_type: params.default_door_type,
      default_door_width_ft: params.default_door_width_ft,
      default_door_height_ft: params.default_door_height_ft,
      unit_overrides: params.unit_overrides?.length ? params.unit_overrides : undefined,
    },
  }
}

/**
 * Generate unit configurations from building and parameters
 */
export function generateUnits(
  _building: GeneratedBuilding,
  params: SingleRowParams
): GeneratedUnit[] {
  const units: GeneratedUnit[] = []

  // Calculate unit widths with overrides
  const { widths } = calculateUnitWidthsWithOverrides(
    params.width_ft,
    params.unit_count,
    params.wall_thickness_ft,
    params.unit_overrides?.filter(o => o.width_ft !== undefined).map(o => ({
      index: o.index,
      width_ft: o.width_ft!,
    }))
  )

  // Calculate unit offsets
  const offsets = calculateUnitOffsets(widths, params.wall_thickness_ft)

  // Interior depth (building width minus walls)
  const interiorDepth = params.depth_ft - 2 * params.wall_thickness_ft

  for (let i = 0; i < params.unit_count; i++) {
    const unitWidth = widths[i]
    const unitDoors = getUnitDoors(params, i, unitWidth, params.unit_count)
    const unitCode = `${params.code}-${String(i + 1).padStart(2, '0')}`

    // Get the first front door's dimensions for unit-level storage (primary door)
    const primaryDoor = unitDoors.front?.[0]

    // Check for type override on this unit
    const typeOverride = params.unit_overrides?.find(o => o.index === i)?.type

    // Create edge classification for this unit with per-wall doors
    // Partition doors (on left/right walls) create pass-through between adjacent units
    const edges = createBoxUnitEdges(
      i,
      params.unit_count,
      params.code,
      unitDoors
    )

    const unit: GeneratedUnit = {
      code: unitCode,
      type: typeOverride || 'box_hangar',
      capacity_strategy: 'single',
      interior_width: unitWidth,
      interior_depth: interiorDepth,
      interior_height: params.height_ft,
      door_width: primaryDoor?.width_ft ?? null,
      door_height: primaryDoor?.height_ft ?? null,
      door_type: primaryDoor?.type,
      offset_x: offsets[i],
      offset_y: 0,
      unit_rotation: 0, // Units in row don't have individual rotation
      edges,
    }

    units.push(unit)
  }

  return units
}
